import React from 'react';
import styled from 'styled-components';
import { rangeDateLabel, daysBetween } from 'TimeOffList/services/format';
import { IconPlus, IconTrash } from 'TimeOffList/components/icons';
import type { IShopClosure } from 'services/types/shopClosure';

interface Props {
  closures: IShopClosure[];
  loading: boolean;
  today: string; // 'YYYY-MM-DD'
  onAdd: () => void;
  onEdit: (c: IShopClosure) => void;
  onDelete: (c: IShopClosure) => void;
}

const ClosureList: React.FC<Props> = ({ closures, loading, today, onAdd, onEdit, onDelete }) => (
  <Wrap>
    <Top>
      <span className="hint">Upcoming and ongoing closed days. Online booking is blocked on these dates.</span>
      <AddBtn onClick={onAdd}><IconPlus size={16} strokeWidth={2.6} />Add Days Off</AddBtn>
    </Top>
    {loading && <Empty>Loading...</Empty>}
    {!loading && closures.length === 0 && <Empty>No upcoming shop days off.</Empty>}
    {closures.map(c => {
      const ongoing = c.startDate <= today && c.endDate >= today;
      const days = daysBetween(c.startDate, c.endDate);
      return (
        <Row key={c.id}>
          <div className="info">
            <span className="date">
              {rangeDateLabel(c.startDate, c.endDate)}
              <em>{days > 1 ? `${days} days` : '1 day'}</em>
              {ongoing && <b>Ongoing</b>}
            </span>
            <span className="reason">{c.reason}</span>
          </div>
          <div className="actions">
            <Btn onClick={() => onEdit(c)}>Edit</Btn>
            <Btn $danger onClick={() => onDelete(c)} aria-label="Delete"><IconTrash size={16} strokeWidth={2.2} /></Btn>
          </div>
        </Row>
      );
    })}
  </Wrap>
);

export default ClosureList;

const Wrap = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  .hint { flex: 1; font-size: 13px; color: var(--fg-3); }
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 11px 20px;
  border-radius: var(--r-md);
  border: none;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  background: var(--brand-navy);
  color: #fff;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: var(--fg-3);
  font-size: 14px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--border-1);
  border-radius: var(--r-lg);
  .info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
  .date { font-size: 15px; font-weight: 700; color: var(--fg-1); display: flex; align-items: center; gap: 8px; }
  .date em { font-style: normal; font-size: 12px; font-weight: 600; color: var(--fg-3); }
  .date b { font-size: 11px; padding: 2px 8px; border-radius: 999px; background: var(--acc-softer); color: var(--acc); }
  .reason { font-size: 13px; color: #2C334B; word-break: break-word; }
  .actions { display: flex; gap: 8px; }
`;

const Btn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 36px;
  padding: 0 14px;
  border-radius: var(--r-md);
  border: 1px solid ${p => (p.$danger ? '#F05326' : 'var(--border-1)')};
  background: #fff;
  color: ${p => (p.$danger ? '#F05326' : 'var(--fg-1)')};
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:hover { background: ${p => (p.$danger ? '#FFEFEB' : '#F5F7FA')}; }
`;
